// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import Clickable from "@insite/mobius/Clickable";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import { CustomAppState } from "@synd/App/Store/Reducers";
import Tooltip from "@synd/MobiusOverrides/Tooltip/Tooltip";
import { breadcrumbsTooltipStyles } from "@synd/XDL/PreStyle/tooltip";
import { color, getSystemColor, hideElementOnPrint, styledLinksMenu } from "@synd/XDL/Utilities";
import * as React from "react";
import styled, { css, withTheme } from "styled-components";

export type BreadcrumbsProps = MobiusStyledComponentProps<
    "nav",
    {
        /** CSS string or styled-components function to be injected into this component.
         * @themable */
        css?: StyledProp<BreadcrumbsProps>;
        /** An array of link props */
        links?: LinkProps[];
        /**
         * Indicates how the `css` property is combined with the variant `css` property from the theme.
         * If true, the variant css is applied first and then the component css is applied after causing
         * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
         */
        mergeCss?: boolean;
        /**
         * Optional props to be passed down to all Link and Typography components.
         * Can be overriden by individual typographyProps in the links array.
         * @themable */
        typographyProps?: TypographyPresentationProps;
        /**
         * XNG-Changes
         * Max number of Links that should be available to overflow
         */
        maxNoOfLinks?: number;
        /**
         * XNG-Changes
         * Max number of Links on Tablet that should be available to overflow
         */
        maxNoOfLinksMobile?: number;
    }
>;

export type BreadcrumbsPresentationProps = Omit<BreadcrumbsProps, "links">;

// export const Slash = styled(Typography as any).attrs({ children: "/" })`
//     margin: 0 10px 5px;
//     display: block;
// `;
//
export const StyledNav = styled.nav.attrs({ "aria-label": "breadcrumbs" })`
    display: flex;
    flex-direction: row;
    align-items: center;
    ${hideElementOnPrint}
    ${injectCss}
`;

const extendedTooltipStyles = {
    tooltipContent: {
        css: css`
            padding: 0;
            border: 1px solid ${getSystemColor("common.ui7", 0.1)};
            > .tooltipInfo {
                ${styledLinksMenu(180)}
                ${IconWrapper} {
                    display: none;
                }
            }
        `,
    },
    tooltipArrow: {
        fill: `${getSystemColor("common.ui1")}`,
        stroke: `${getSystemColor("common.ui7", 0.1)}`,
    },
};

const Breadcrumbs: React.FC<BreadcrumbsProps> = withTheme(
    ({ links, mergeCss, maxNoOfLinks: maxNoOfLinksProps = 6, maxNoOfLinksMobile = 3, ...otherProps }) => {
        const { spreadProps, applyStyledProp } = applyPropBuilder(otherProps, { component: "breadcrumbs" });
        const typographyProps = spreadProps("typographyProps");
        const resolvedMergeCss = mergeCss ?? otherProps?.theme?.breadcrumbs?.defaultProps?.mergeCss;

        // XNG-Changes
        const [isActive, setActive] = React.useState(false);
        const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon?.isTabletScreen);

        // return null if `links` is an empty object, collection, map, or set.
        if (!links || links.length === 0) {
            return null;
        }
        const children = links.map((link: any, index: number) => {
            const children = link.children;
            // yield null if no children
            if (!React.Children.count(children)) {
                return null;
            }
            const key = typeof children === "string" ? `${children}_${index}` : index;
            if (index === links.length - 1) {
                return (
                    <Typography
                        mergeCss={resolvedMergeCss}
                        {...typographyProps}
                        weight="bold"
                        aria-current="page"
                        key={key}
                        data-test-selector={`breadCrumb_${children}`}
                    >
                        {children}
                    </Typography>
                );
            }
            return (
                <React.Fragment key={key}>
                    <Link
                        mergeCss={true}
                        typographyProps={typographyProps}
                        // XNG-Changes
                        color="common.ui7"
                        {...link}
                        css={css`
                            &:focus {
                                outline: none;
                            }
                            &:hover > ${TypographyStyle} {
                                color: ${color("text.link")};
                            }
                        `}
                    />
                    <Icon size={12} {...typographyProps} src={ChevronRight} />
                </React.Fragment>
            );
        });
        // return null if `links` map yields an array of nulls
        if (Object.keys(children.filter((x: typeof Link) => x !== null)).length === 0) {
            return null;
        }

        // XNG-Changes
        const maxNoOfLinks = isTabletScreen && maxNoOfLinksMobile ? maxNoOfLinksMobile : maxNoOfLinksProps;
        const tooltipContent = !isActive && children.length >= maxNoOfLinks && children.slice(1, children.length - 1);
        if (tooltipContent && tooltipContent.length > 0) {
            return (
                <StyledNav {...otherProps} css={applyStyledProp("css", resolvedMergeCss)}>
                    {children[0]}
                    <Tooltip
                        text={children.slice(1, children.length - 1)}
                        triggerComponent={
                            <Clickable
                                data-test-selector="breadcrumbs_menu"
                                aria-label="ellipsis"
                                onClick={() => setActive(true)}
                            >
                                <Typography {...typographyProps} aria-current="page" key="otherBreadcrumbs">
                                    ...
                                </Typography>
                            </Clickable>
                        }
                        mergeCss={true}
                        extendedStyles={breadcrumbsTooltipStyles}
                        typographyProps={{
                            css: css`
                                ${styledLinksMenu(180)}
                            `,
                        }}
                    />
                    <Icon size={12} {...typographyProps} src={ChevronRight} />
                    {children[children.length - 1]}
                </StyledNav>
            );
        }

        return (
            <StyledNav {...otherProps} css={applyStyledProp("css", resolvedMergeCss)}>
                {children}
            </StyledNav>
        );
    },
);

Breadcrumbs.displayName = "Breadcrumbs";

/** @component */
export default Breadcrumbs;
